import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import ReturnFormItem from './ui/ReturnFormItem';
import {
  ReturnFormActions,
  Status,
  submitForm,
} from '../../Reducers/ReturnForm.reducer';
import { useDispatch } from 'react-redux';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import RadioButton from '../Inputs/Radiobutton';
import { MyPagesReturn } from './Types/MyPages';
import EditorString from '../EditorString/EditorString';
import TextArea from '../Inputs/TextArea';
import {
  ReturnFormItemValidationSchema,
  ReturnFormValidationSchema,
} from './misc/ReturnFormValidationSchema';
import { ValidationError } from 'yup';
import { triggerToast } from '../../Services/Toast.service';
import LoadingSpinner from '../LoadingSpinner';
import ReturnFormSuccessContainer from './ui/ReturnFormSuccessContainer';

const MyPagesReturnForm = (props: MyPagesReturn) => {
  const returnForm = useTypedSelector((state) => state.returnForm);

  const { returns, returnType, comment, status, errors } = returnForm;

  const {
    title,
    description,
    termsTitle,
    termsText,
    errorCodes,
    confirmationText,
  } = props;

  const dispatch = useDispatch();

  const [valid, setValid] = useState<boolean>(true);

  useEffect(() => {
    if (status == Status.rejected) {
      setTimeout(() => {
        dispatch(
          ReturnFormActions.setReturnStatus({
            status: Status.idle,
          })
        );
      }, 1500);
    }
  }, [status]);

  const submitButtonContent = useCallback(() => {
    switch (status) {
      case Status.idle:
        return <>{translate('contact-form.submit')}</>;
      case Status.pending:
        return <LoadingSpinner />;
      case Status.fulfilled:
        return <Icon name="check" className="h-6 w-6" />;
      case Status.rejected:
        return <Icon name="close" className="h-6 w-6" />;
      default:
        return null;
    }
  }, [status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch(
      ReturnFormActions.setReturnStatus({ status: Status.pending })
    );

    dispatch(ReturnFormActions.clearErrors());

    let valid = true;

    try {
      await ReturnFormValidationSchema.validate(returnForm, {
        abortEarly: false,
      });
    } catch (err) {
      valid = false;

      dispatch(
        ReturnFormActions.setReturnError({
          //Only 1 validation on the form itself
          error: {
            title: translate('return.form-general.error-title'),
            error: translate('return.form-credit.type-error'),
          },
        })
      );
      dispatch(
        ReturnFormActions.setReturnStatus({
          status: Status.rejected,
        })
      );

      return;
    }

    Promise.all(
      returns.map(async (r) => {
        try {
          await ReturnFormItemValidationSchema.validate(r, {
            abortEarly: false,
          });
        } catch (err) {
          valid = false;
          if (err instanceof ValidationError) {
            const validationErr = err as ValidationError;
            const newValidationErrors = validationErr.inner.reduce(
              (prev, e) => ({ ...prev, ['' + e.path]: e.message }),
              {}
            );

            for (const property in newValidationErrors) {
              if (newValidationErrors.hasOwnProperty(property)) {
                const error = newValidationErrors[property];
                dispatch(
                  ReturnFormActions.setRowValidationErrors({
                    id: r.uniqueId,
                    key: property,
                    error: error,
                  })
                );
                dispatch(
                  ReturnFormActions.setReturnStatus({
                    status: Status.rejected,
                  })
                );
              }
            }
          }
          return;
        }
      })
    ).finally(() => {
      if (valid) {
        dispatch(submitForm(returnForm));
      }
    });
  };

  useEffect(() => {
    errors?.length > 0 &&
      errors?.map((error) => {
        triggerToast({
          title: error.title,
          text: error.error,
          type: 'error',
          autoClose: false
        });
      });
  }, [errors, dispatch]);

  if (status == Status.fulfilled) {
    return (
      <ReturnFormSuccessContainer confirmationText={confirmationText} />
    );
  }

  return (
    <div className="col-span-2 flex flex-col gap-6">
      <div>
        {title && <h1 className="mb-2">{title}</h1>}
        {description && <EditorString value={description.value} />}
      </div>
      <div>
        <p className="text-lg font-bold text-gray-700">
          {translate('return.form-description')}
        </p>
        <p>{translate('return.form-description.disclaimer')}</p>
      </div>
      <div>
        {returns?.length > 0 &&
          returns.map((item, idx) => {
            return (
              <ReturnFormItem
                item={item}
                index={idx + 1}
                key={idx}
                errorCodes={errorCodes}
              />
            );
          })}
        <button
          className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-solid border-gray-700 px-4 py-2 text-center text-sm font-normal uppercase"
          onClick={() => dispatch(ReturnFormActions.addReturnItem())}
        >
          <Icon name="plus" className="h-5 w-5" />
          {translate('quick-order.add-row')}
        </button>
      </div>

      <div className="col-span-full flex flex-col gap-4">
        <div>
          <p className="text-lg font-bold text-gray-700">
            {translate('return.form-credit.type')}
          </p>
          <p className="text-lg font-light text-gray-700">
            {translate('return.form-credit.type-description')}
          </p>
        </div>
        <RadioButton
          name="return-type"
          label={translate('return.form-credit.type-option.replacement')}
          value={translate('return.form-credit.type-option.replacement')}
          onChange={(e) =>
            dispatch(
              ReturnFormActions.setReturnType({ value: e.target.value })
            )
          }
        />
        <RadioButton
          name="return-type"
          label={translate('return.form-credit.type-option.credit')}
          value={translate('return.form-credit.type-option.credit')}
          id="returnType"
          onChange={(e) =>
            dispatch(
              ReturnFormActions.setReturnType({ value: e.target.value })
            )
          }
        />
      </div>
      <div className="col-span-full flex flex-col gap-4">
        <div>
          <p className="text-lg font-bold text-gray-700">
            {translate('return.form-message')}
          </p>
          <p className="text-lg font-light text-gray-700">
            {translate('return.form-message.text')}
          </p>
        </div>
        <TextArea
          placeholder={translate('return.form-message.placeholder')}
          onChange={(e) =>
            dispatch(
              ReturnFormActions.setReturnComment({ value: e.target.value })
            )
          }
        />
        <button
          className="btn btn--black btn--xlarge w-full"
          onClick={(e) => handleSubmit(e)}
        >
          {submitButtonContent()}
        </button>
      </div>
    </div>
  );
};

export default MyPagesReturnForm;
